import React, { useEffect, useState } from "react";
import Pulse from "../Loader/Pulse";
import ServerRequest from "../../exports/ServerRequest";
import {
  Add,
  OrderBook,
  Cross,
  Refresh,
} from "../CustomComponents/SwiftIcon/Icons";
import SwiftModal from "../CustomComponents/SwiftModal/SwiftModal";
import { COLOR_VARS } from "../../exports/ColorVars";
import { getDeviceSize } from "../../exports/InteractiveUtils";
import WatchlistPopup from "../Body/AppPopups/WatchlistPopup/WatchlistPopup";
import { Alert } from "../Body/CustomChartComponents/CustomAlert/CustomAlert";
import "../../css/AIResearch/AiResearch.css";
import { GetCookie } from "../../exports/CookieManagement";
import Badge from "../ui/badge";
import SwiftToolTip from "../CustomComponents/SwiftToolTip/SwiftToolTip";

const DEVICE_SIZE = getDeviceSize();

const warning_config = {
  Band: true,
  BandColor: "#470985",
  BoxColor: "#ffffff",
  TextColor: "#000000",
  AutoClose: {
    Active: true,
    Line: true,
    LineColor: "#470985",
    Time: 3,
  },
};

/** 🔥 TYPE HELPER */
const getType = (i) => {
  const seg = i.segment;

  if (seg === "INDICES") return "Indices";
  if (["NSE", "BSE", "NCO"].includes(seg)) return "Stock";
  if (["NFO-FUT", "BFO-FUT", "CDS-FUT", "MCX-FUT", "NCO-FUT"].includes(seg))
    return "Futures";
  if (["NFO-OPT", "BFO-OPT", "CDS-OPT", "MCX-OPT", "NCO-OPT"].includes(seg))
    return "Options";

  return "Other";
};

/** 🔥 EXPIRY CHECK (ONLY USED WITH COMPLETE STATUS) */
const isExpired = (timestamp) => {
  if (!timestamp) return false;

  const now = new Date();
  const reportDate = new Date(timestamp);

  const diffTime = now - reportDate;
  const diffDays = diffTime / (1000 * 60 * 60 * 24);

  return diffDays > 3;
};

const AIResearchContainer = () => {
  const token = GetCookie("token");

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMsg, setLoadingMsg] = useState("Fetching AI reports...");
  const [opacityMsg, setOpacityMsg] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [viewReportCode, setViewReportCode] = useState(null);
  const [iframeLoading, setIframeLoading] = useState(true);

  useEffect(() => {
    if (viewReportCode) {
      setIframeLoading(true);
    }
  }, [viewReportCode]);

  useEffect(() => {
    fetchReports();
  }, []);

  /** 🔄 Loader animation */
  useEffect(() => {
    if (!loading) return;

    const msgInterval = setInterval(() => {
      setLoadingMsg((prev) =>
        prev === "Fetching AI reports..."
          ? "Preparing analysis..."
          : "Fetching AI reports...",
      );
    }, 3000);

    const opacityInterval = setInterval(() => {
      setOpacityMsg((prev) => !prev);
    }, 1500);

    return () => {
      clearInterval(msgInterval);
      clearInterval(opacityInterval);
    };
  }, [loading]);

  /** 🔥 FETCH */
  const fetchReports = async () => {
    try {
      setLoading(true);
      const res = await ServerRequest({
        url: `/allreports?token=${token}`,
        method: "GET",
      });

      if (res.success) {
        const sorted = (res.data || []).sort((a, b) => {
          const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
          const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
          return timeB - timeA;
        });
        setReports(sorted);
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setLoading(false);
    }
  };

  /** 🔥 ADD REPORT */
  const handleAddSymbol = async (stock) => {
    setShowPopup(false);

    try {
      const code = stock.nse_code || stock.code;

      const exists = reports.find((r) => r.code === code);
      if (exists) {
        Alert({
          TitleText: "Warning",
          Message: "Report already exists",
          ...warning_config,
        });
        return;
      }

      const type = getType(stock);

      const res = await ServerRequest({
        url: `/run/report?token=${token}`,
        method: "POST",
        data: {
          action: "run",
          code,
          name: stock.company,
          trading_symbol: stock.nse_code || stock.code,
          type,
        },
      });

      if (res?.job?.status === "pending") {
        Alert({
          TitleText: "Report Queued",
          Message: `${stock.company} added for AI report generation`,
          ...warning_config,
        });
        fetchReports();
      } else {
        alert("Something went wrong. Try again.");
      }
    } catch (err) {
      console.error("Run report failed", err);
      alert("Failed to add report");
    }
  };

  /** 🔥 RE-RUN */
  const handleReRun = async (item) => {
    try {
      const res = await ServerRequest({
        url: `/run/report?token=${token}`,
        method: "POST",
        data: {
          action: "run",
          code: item.code,
          name: item.name,
          trading_symbol: item.trading_symbol,
          type: item.type || "Stock",
        },
      });

      if (res?.job?.status === "pending") {
        Alert({
          TitleText: "Re-run Started",
          Message: `${item.name} report is regenerating`,
          ...warning_config,
        });

        fetchReports();
      }
    } catch (err) {
      console.error(err);
      alert("Failed to rerun report");
    }
  };

  /** ================= UI ================= */

  if (loading) {
    return (
      <div className="mf-wrapper">
        <div className="app__login__screen">
          <div className="app__login__process">
            <Pulse />
            <p className={opacityMsg ? "fadeIn" : "fadeOut"}>{loadingMsg}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="strategy-content">
      <div className="mf-wrapper">
        <div className="ai-research-section">
          <div className="ai-research-header">
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <p>AI Research Reports</p>
              <button className="refresh-btn" onClick={() => fetchReports()}>
                <Refresh size={12} color="currentColor" /> Refresh
              </button>
            </div>

            <div className="ai-report-section-button">
              <div
                className="swift-button-icon"
                onClick={() => setShowPopup(true)}
              >
                Add Report
              </div>
            </div>
          </div>

          <div className="mf-research-container">
            {reports.length === 0 ? (
              <div className="mf-empty">
                <OrderBook size={40} color={COLOR_VARS["SWIFT_COLOR4"]} />
                <div>
                  <p>No Reports Found</p>
                  <span>No AI research reports available yet</span>
                </div>
              </div>
            ) : DEVICE_SIZE === "S" ? (
              <div
                style={{ display: "flex", flexDirection: "column", gap: "5px" }}
              >
                {reports.map((item, index) => (
                  <ReportCard
                    key={index}
                    data={item}
                    onReRun={handleReRun}
                    onViewReport={(code) => setViewReportCode(code)}
                  />
                ))}
              </div>
            ) : (
              <ReportTable
                reports={reports}
                onReRun={handleReRun}
                onViewReport={(code) => setViewReportCode(code)}
              />
            )}
          </div>
        </div>
      </div>

      {showPopup && (
        <WatchlistPopup
          ClosePopup={() => setShowPopup(false)}
          AddSymbol={handleAddSymbol}
        />
      )}

      {viewReportCode && (
        <SwiftModal
          closeModal={() => setViewReportCode(null)}
          top={DEVICE_SIZE === "S" ? "0%" : "5%"}
        >
          <div
            className="ai-report-modal-content"
            style={{
              width: DEVICE_SIZE === "S" ? "100vw" : "content-fit",
              minWidth: DEVICE_SIZE === "S" ? "100%" : "70vw",
              height: DEVICE_SIZE === "S" ? "100vh" : "85vh",
              borderRadius: DEVICE_SIZE === "S" ? "0" : "8px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "15px 20px",
                borderBottom: "1px solid var(--border-color)",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontWeight: "bold",
                  fontSize: "14px",
                  color: "var(--text-color)",
                }}
              >
                Report: {viewReportCode}
              </p>
              <div
                style={{ cursor: "pointer", display: "flex" }}
                onClick={() => setViewReportCode(null)}
              >
                <Cross size={24} color={COLOR_VARS["SWIFT_COLOR4"]} />
              </div>
            </div>
            {iframeLoading && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flex: 1,
                  width: "100%",
                }}
              >
                <Pulse />
              </div>
            )}
            <iframe
               src={`/public/report/${viewReportCode}`}
               style={{
                 width: "100%",
                 flex: 1,
                 border: "none",
                 display: iframeLoading ? "none" : "block",
               }}
               title="Report Viewer"
               onLoad={() => setIframeLoading(false)}
             />
          </div>
        </SwiftModal>
      )}
    </div>
  );
};

/** ================= TABLE ================= */

const ReportTable = ({ reports, onReRun, onViewReport }) => {
  return (
    <table className="ai-report-table">
      <thead>
        <tr>
          <th>Code</th>
          <th>Name</th>
          <th>Recommendation</th>
          <th>Date</th>
          <th className="action-header">Actions</th>
        </tr>
      </thead>

      <tbody>
        {reports.map((item, index) => (
          <tr key={index}>
            <td>{item.code}</td>
            <td>{item.name}</td>
            <td>
              {item.recommendation ? (
                <Badge
                  variant={
                    item.recommendation.toLowerCase() === "buy"
                      ? "success"
                      : item.recommendation.toLowerCase() === "sell"
                        ? "error"
                        : "warning"
                  }
                  size="xxs"
                >
                  {item.recommendation.toUpperCase()}
                </Badge>
              ) : (
                "-"
              )}
            </td>

            <td>
              {item.timestamp ? new Date(item.timestamp).toLocaleString() : "-"}
            </td>

            <td>
              <div className="action-cell">
                <SwiftToolTip
                  title={
                    item.status === "complete" && isExpired(item.timestamp)
                      ? "It can take up to 15 mins"
                      : "Please wait for 3 days before running another AI analysis."
                  }
                  placement="bottom"
                >
                  <Badge
                    onClick={() => {
                      if (
                        item.status === "complete" &&
                        isExpired(item.timestamp)
                      ) {
                        onReRun(item);
                      }
                    }}
                    className="rerun-badge"
                    style={
                      item.status === "complete" && isExpired(item.timestamp)
                        ? { cursor: "pointer" }
                        : { opacity: 0.8, cursor: "not-allowed" }
                    }
                    size="xxs"
                    variant="warning"
                  >
                    ↻ Re-run
                  </Badge>
                </SwiftToolTip>
                {item.status === "complete" ? (
                  <button
                    className="btn-outline view-btn"
                    onClick={() => onViewReport(item.code)}
                  >
                    View Report
                  </button>
                ) : (
                  <span className="processing-text">Processing...</span>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/** ================= CARD ================= */

const ReportCard = ({ data, onReRun, onViewReport }) => {
  return (
    <div className="ai-research-card">
      <div className="airc-info"></div>

      <div className="airc-config">
        <div className="airc-config-row">
          <p className="name">{data.name}</p>
          <p>
            <span>
              {data.timestamp
                ? new Date(data.timestamp).toLocaleDateString()
                : "-"}
            </span>
          </p>
        </div>

        <div className="airc-config-row">
          <p>
            <Badge
              variant={
                data.recommendation?.toLowerCase() === "buy"
                  ? "success"
                  : data.recommendation?.toLowerCase() === "sell"
                    ? "error"
                    : "warning"
              }
              size="xxs"
            >
              {data.recommendation ? data.recommendation.toUpperCase() : "Analysing"}
            </Badge>
          </p>
          <div style={{display:"flex", gap:"5px", alignItems:"center"}}>
            {" "}
            {data.status === "complete" ? (
              <button
                className="btn-outline view-btn"
                onClick={() => onViewReport(data.code)}
              >
                <span>View</span>
              </button>
            ) : (
              <p style={{ fontSize: "12px", color: "var(--font-secondary)" }}>
                Processing...
              </p>
            )}
            <SwiftToolTip
              title={
                data.status === "complete" && isExpired(data.timestamp)
                  ? "It can take up to 15 mins"
                  : "You can rerun after 3 days"
              }
              placement="bottom"
            >
              <Badge
                variant="warning"
                size="xxs"
                className="rerun-badge"
                style={
                  data.status === "complete" && isExpired(data.timestamp)
                    ? { cursor: "pointer" }
                    : { opacity: 0.8, cursor: "not-allowed" }
                }
                onClick={() => {
                  if (data.status === "complete" && isExpired(data.timestamp)) {
                    onReRun(data);
                  }
                }}
              >
                Re-run
              </Badge>
            </SwiftToolTip>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIResearchContainer;
